// src/sagas/matrix.sagas.js

/**
 * @module sagas/matrix.sagas
 *
 * Services
 *
 * 👉 matrix: use the apiFetch to initialize polling (pull data from warehouse)
 * 👉 build the spec using a single graphql call to obs-rs
 *
 */
import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  MATRIX,
  FETCH_MATRIX,
  FETCH_MATRIX_CACHE,
  setMatrixCache,
} from '../ducks/actions/matrix.actions';
import { setNotification } from '../ducks/actions/notifications.actions';
import { getProjectId, isHostedMatrixStale } from '../ducks/rootSelectors';
import { initAbortController } from '../../hooks/use-abort-controller';
// -----------------------------------------------------------------------------
// 📡 direct calls to fetch (see api MATRIX)
//
import {
  fetchMatrixSpec as fetchMatrixSpecInner, // generates the matrix request
  fetchRequestFieldNames as fetchFieldNamesInner, // preview field names
} from '../services/api';
// indirect - fetch by way of api polling-machine
import { apiFetch } from '../ducks/actions/api.actions';
// -----------------------------------------------------------------------------
import { setUiLoadingState } from '../ducks/actions/ui.actions';
import * as UT from './sagas.helpers';

import {
  requestFromTree,
  requestFromNode,
  withDerivedFields,
} from '../lib/obsEtlToMatrix/matrix-request';
import {
  SagasError,
  InvalidStateError,
  GqlError,
  ApiCallError,
} from '../lib/LuciErrors';
import { colors } from '../constants/variables';

//------------------------------------------------------------------------------
const DEBUG =
  process.env.REACT_APP_DEBUG_MIDDLEWARE === 'true' ||
  process.env.REACT_APP_DEBUG_MATRIX === 'true';
const COLOR = colors.light.blue;
//------------------------------------------------------------------------------
/* eslint-disable no-console */

const MAX_TRIES = process.env.REACT_APP_MATRIX_MAX_TRIES || 30;

const fetchRequestFieldNames = (projectId, request, signal) =>
  fetchFieldNamesInner({ projectId, request, signal });

const fetchMatrixSpec = (projectId, request, signal) =>
  fetchMatrixSpecInner({ projectId, request, signal });

if (DEBUG) {
  console.info(`%c👉 matrix.sagas`, COLOR);
}

// get a request from a single node
function* _queueMatrixCache({ payload }) {
  const abortController = initAbortController();
  try {
    yield put(
      setNotification({
        message: 'Background: Cache request in process',
        feature: MATRIX,
      }),
    );
    const flatTree = yield select((state) => state.workbench.tree);
    const { id, displayType } = payload;

    // requestFromNode depends on id and displayType hosted in payload
    const { fields: request } = requestFromNode(flatTree, { id, displayType });
    const projectId = yield select(getProjectId);

    if (DEBUG) {
      console.debug(`%c📦 cache: single GQL call for field names`, COLOR);
      console.dir(request);
    }

    // Single GQL call — obs-rs handles the full request
    const response = yield call(
      fetchRequestFieldNames,
      projectId,
      request,
      abortController.signal,
    );

    //
    // [[fieldNames]]
    // Extract field names from the single response
    //
    const fieldNames = (response.data?.meaExpressions ?? []).map(({ filter }) => {
      return filter.fieldName.value;
    });

    if (DEBUG) {
      console.debug(`%c📦 cache: received ${fieldNames.length} field names`, COLOR);
    }

    yield put(
      setMatrixCache({
        payload: [fieldNames],
        meta: payload,
      }),
    );
  } catch (e) {
    if (e instanceof GqlError) throw e;
    throw new SagasError('Queue matrix cache request failed', e);
  }
}

/**
 *
 * Pull the configuration made using the workbench to generate the request.
 * Uses a single graphql call to obs-rs to build the complete matrix spec.
 * Forward the completed spec to tnc-py for data retrieval (warehouse -> matrix).
 *
 */
function* _queueMatrixRequest(action) {
  //
  const abortController = action?.abortController ?? initAbortController();
  const requestedProject = action?.projectId;

  try {
    // guard - run when hostedMatrixState = 'STALE'
    const isStale = yield select(isHostedMatrixStale);
    //
    if (isStale) {
      yield put(
        setUiLoadingState({
          toggle: true,
          feature: MATRIX,
          message: 'Queued the matrix request',
        }),
      );

      // 1. build the request using a single graphql call
      const request = yield buildMatrixSpec(abortController);

      // 2. engage the tnc-py polling api with the now completed request
      const projectInRedux = yield select(getProjectId);
      if (projectInRedux !== requestedProject) {
        throw new InvalidStateError(
          `Matrix request failed: invalid project state ${JSON.stringify(
            action,
            null,
            2,
          )}`,
          new Error().stack,
        );
      }
      try {
        //
        yield put(
          apiFetch(
            {
              // ::event (see middleware CANCEL to aligh uiKey)
              meta: { uiKey: action.payload, feature: MATRIX },
              request: {
                project_id: requestedProject,
                spec: request,
                maxTries: MAX_TRIES,
                signal: abortController.signal,
              },
            }, // map + translation
          ),
        );
      } catch (e) {
        throw new ApiCallError(`Queuing the matrix request failed`, e);
      }
    }
  } catch (e) {
    abortController.abort();
    if (e instanceof InvalidStateError) throw e;
    if (e instanceof ApiCallError) throw e;
    if (e instanceof GqlError) throw e;
    if (e instanceof SagasError) throw e;
    throw new SagasError(
      `Unknown error with the matrix request: -${requestedProject.slice(-4)}`,
      e,
    );
  }
}

/*-----------------------------------------------------------------------------*/
// Local functions
/*-----------------------------------------------------------------------------*/
/**
 * Single GQL call to obs-rs to build the complete matrix spec.
 * obs-rs accepts the full request (subReq + all meaReqs) and returns
 * a deduplicated spec using BTreeMap<MeaReqKey, ReqComponents>.
 *
 * @function
 * @return {MatrixRequestSpec}
 */
function* buildMatrixSpec(abortController) {
  try {
    //
    // 👉 Pull the tree from state
    //
    const flatTree = yield select((state) => state.workbench.tree);
    //
    // 👉 transform: tree -> request for gql obs service
    //
    const { fields: request, derivedFields } = yield requestFromTree(flatTree);

    //
    if (DEBUG) {
      console.debug(`%c1️⃣ Request from workbench: Tree -> Request`, COLOR);
      console.dir(request);
      console.dir(derivedFields);
    }

    // ⌛ Single GQL call — obs-rs handles all meaReqs in one request
    const projectId = yield select(getProjectId);

    if (DEBUG) {
      console.debug(`%c2️⃣ Sending single GQL request to obs-rs`, COLOR);
    }

    const response = yield call(
      fetchMatrixSpec,
      projectId,
      request,
      abortController.signal,
    );

    const matrixSpec = response.data;

    if (DEBUG) {
      console.debug(
        `%c3️⃣ GQL response: ${matrixSpec.fieldCount} fields, ${matrixSpec.meaExpressions.length} meaExpressions`,
        COLOR,
      );
      console.dir(matrixSpec);
    }

    //
    // 🔖 Derived fields are separate from obs/mms that hosts single-source of truth
    //    Thus, append derived fields.
    //
    const finalRequest = yield withDerivedFields(matrixSpec, derivedFields);

    if (DEBUG) {
      console.debug(`%c4️⃣ finalSpec: ...with derived fields`, COLOR);
      console.dir(finalRequest);
    }

    return finalRequest;
  } catch (e) {
    abortController.abort();
    if (e instanceof GqlError) throw e;
    throw new SagasError(`Matrix failed to build the request`, e);
  }
}

// -----------------------------------------------------------------------------
// **NOTE** anything exported from this file will be executed by src/initSagas
// -----------------------------------------------------------------------------
// Watcher
// Use the output of the workbench (config) to warehouse -> matrix
export function* watchForMatrixRequest() {
  UT.log(yield takeLatest(FETCH_MATRIX, _queueMatrixRequest), `taking ${FETCH_MATRIX}`);
}
export function* watchForMatrixCache() {
  UT.log(
    yield takeLatest(FETCH_MATRIX_CACHE, _queueMatrixCache),
    `taking ${FETCH_MATRIX_CACHE}`,
  );
}
